import { useState } from 'react'

/**
 * Pagination state management for offset-based pagination
 *
 * Provides pagination state with navigation methods.
 * Follows backend API pattern: limit (default 20) and offset for page positioning.
 *
 * @param initialLimit - Items per page (default: 20 per spec)
 * @param initialOffset - Starting offset (default: 0)
 * @returns Object with limit, offset, navigation methods, and page info
 *
 * @example
 * ```tsx
 * const { limit, offset, nextPage, prevPage, reset, canGoNext, currentPage } = usePagination(20, 0);
 *
 * // Use limit and offset for API calls
 * const { data } = useQuery({
 *   queryKey: ['moves', limit, offset],
 *   queryFn: () => fetchMoves({ limit, offset }),
 * });
 *
 * // Navigation buttons
 * <button onClick={prevPage} disabled={!canGoPrev}>Previous</button>
 * <button onClick={nextPage} disabled={!canGoNext(totalCount)}>Next</button>
 * ```
 */
export function usePagination(
  initialLimit: number = 20,
  initialOffset: number = 0
) {
  const [limit, setLimit] = useState<number>(initialLimit)
  const [offset, setOffset] = useState<number>(initialOffset)

  const nextPage = () => {
    setOffset(prev => prev + limit)
  }

  const prevPage = () => {
    setOffset(prev => Math.max(0, prev - limit))
  }

  const goToPage = (page: number) => {
    setOffset(page * limit)
  }

  const reset = () => {
    setOffset(0)
  }

  const changeLimit = (newLimit: number) => {
    setLimit(newLimit)
    setOffset(0) // Reset to first page when changing limit
  }

  // Computed values
  const currentPage = Math.floor(offset / limit)
  const canGoPrev = offset > 0
  const canGoNext = (totalCount?: number) => {
    if (totalCount === undefined) return true // Unknown total, assume more pages
    return offset + limit < totalCount
  }

  return {
    limit,
    offset,
    nextPage,
    prevPage,
    goToPage,
    reset,
    changeLimit,
    currentPage,
    canGoPrev,
    canGoNext,
  }
}
